import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";

import PlusIcon from '@rsuite/icons/Plus';

import 'ckeditor5/ckeditor5.css';
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';
export default function TissList({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
    const { t } = useTranslation();
    let cookie = getCookies();

    // Helper to get multilingual value
    const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
        if (!field) return fallback;
        if (typeof field === 'string') return field; // Legacy support
        if (typeof field === 'object') {
            return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
        }
        return fallback;
    };

    // Helper to set multilingual value in list items
    const normalizeListForLanguage = (list: any[], lang: LanguageCode): any[] => {
        return list.map(item => ({
            ...item,
            text: getMultilingualValue(item.text, lang, item.text || '')
        }));
    };

    // Initialize multilingual list from props
    const initializeMultilingualList = () => {
        const rawList = data.data?.list || [];
        return rawList.map((item: any) => ({
            ...item,
            // Ensure text is preserved as multilingual object
            text: typeof item.text === 'string' 
                ? { en: item.text } 
                : (item.text || {})
        }));
    };

    // State - Store ALL multilingual list data (all 5 languages at once)
    const [multilingualList, setMultilingualList] = React.useState<any[]>(initializeMultilingualList());
    
    // Get current language list from multilingual state
    const getCurrentListForLang = () => {
        return multilingualList.map(item => ({
            ...item,
            text: getMultilingualValue(item.text, activeLang, '')
        }));
    };
    
    const currentList = getCurrentListForLang();
    const hasContent = currentList.length > 0;

    const [isEditing, setIsEditing] = React.useState(!hasContent);
    const [text, setText] = React.useState<string>('');
    const [saving, setSaving] = React.useState(false);
    const [hasChanges, setHasChanges] = React.useState(false);
    const [movingIndex, setMovingIndex] = React.useState<number | null>(null);
    const [deletingIndex, setDeletingIndex] = React.useState<number | null>(null);
    const prevLangRef = React.useRef(activeLang);
    const prevDataIdRef = React.useRef(data?._id);

    // Update multilingual list when data from server changes
    React.useEffect(() => {
        if (prevDataIdRef.current !== data?._id) {
            const rawList = data.data?.list || [];
            
            // Initialize multilingual list from server
            const newMultilingualList = rawList.map((item: any) => ({
                ...item,
                // Ensure text is preserved as multilingual object
                text: typeof item.text === 'string' 
                    ? { en: item.text } 
                    : (item.text || {})
            }));
            
            setMultilingualList(newMultilingualList);
            setHasChanges(false);
            prevDataIdRef.current = data?._id;
        }
        
        // Update ref when language changes
        if (prevLangRef.current !== activeLang) {
            setHasChanges(false);
            prevLangRef.current = activeLang;
        }
    }, [data?._id, data?.data?.list, activeLang]);

    // Track changes - compare current list with multilingual state
    React.useEffect(() => {
        const originalList = data.data?.list || [];
        const originalForLang = normalizeListForLanguage(originalList, activeLang);
        const dataEqual = JSON.stringify(currentList) === JSON.stringify(originalForLang);
        setHasChanges(!dataEqual);
    }, [currentList, activeLang, data?.data?.list]);

    const AddText = () => {
        if (!text.trim()) return;
        
        // Add new item to multilingual list
        const newItem = {
            text: { [activeLang]: text.trim() },
            arrange: multilingualList.length + 1,
        };
        
        setMultilingualList((prev: any[]) => [...prev, newItem]);
        setText('');
    };

    const removeItem = async (index: number) => {
        const result = await Swal.fire({
            title: t('Delete item?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (!result.isConfirmed) return;

        setDeletingIndex(index);
        try {
            setMultilingualList((prev: any[]) => 
                prev.filter((_, i) => i !== index).map((item, idx) => ({
                    ...item,
                    arrange: idx + 1,
                }))
            );
        } finally {
            setDeletingIndex(null);
        }
    };

    const moveItem = async (index: number, direction: 'up' | 'down') => {
        setMovingIndex(index);
        try {
            setMultilingualList((prev: any[]) => {
                const newList = [...prev];
                const targetIndex = direction === 'up' ? index - 1 : index + 1;

                if (targetIndex >= 0 && targetIndex < newList.length) {
                    // Swap items
                    [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
                    // Update arrange values
                    newList[index].arrange = index + 1;
                    newList[targetIndex].arrange = targetIndex + 1;
                }
                return newList;
            });
        } finally {
            setTimeout(() => setMovingIndex(null), 300);
        }
    };

    const editItem = (index: number, newText: string) => {
        setMultilingualList((prev: any[]) => 
            prev.map((item, idx) => {
                if (idx === index) {
                    // Update only the current language, preserve all others
                    const currentText = item.text || {};
                    const updatedText = typeof currentText === 'string' 
                        ? { [activeLang]: newText }
                        : { ...currentText, [activeLang]: newText };
                    return { ...item, text: updatedText };
                }
                return item;
            })
        );
    };

    const save = async () => {
        if (!data?._id) return;

        setSaving(true);
        try {
            console.log('=== SAVING ALL MULTILINGUAL LIST ===');
            console.log('All languages in state:', multilingualList);
            
            // Send ALL multilingual list to backend
            const dataToSend = {
                list: multilingualList // Send complete multilingual list
            };
            
            console.log('Sending to backend:', JSON.stringify(dataToSend, null, 2));

            await axios({
                method: 'put',
                data: dataToSend,
                url: `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                },
            });
            Swal.fire({
                icon: 'success',
                title: t('Saved successfully'),
                timer: 1200,
                showConfirmButton: false
            });
            setIsEditing(false);
            setHasChanges(false);
            if (reload) reload();
        } catch (error) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: t('Error saving'),
                icon: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges) {
            Swal.fire({
                title: t('Discard changes?'),
                text: t('You have unsaved changes. Are you sure you want to cancel?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('Yes, discard'),
                cancelButtonText: t('No, keep editing'),
            }).then((result) => {
                if (result.isConfirmed) {
                    // Reload from server to reset all changes
                    reload();
                    setIsEditing(false);
                    setHasChanges(false);
                }
            });
        } else {
            setIsEditing(false);
        }
    };

    // Empty state
    if (!hasContent && !isEditing) {
        return (
            <div
                className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
                onClick={() => setIsEditing(true)}
                dir="rtl"
            >
                <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </div>
                <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
                    {t('Click to add list')}
                </span>
                <span className="text-gray-400 text-xs mt-1">
                    {t('Create a bulleted or numbered list')}
                </span>
            </div>
        );
    }

    // Preview Mode
    if (!isEditing) {
        return (
            <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
                <div className="p-0.5 relative">
                    {currentList.length > 0 ? (
                        <div className="space-y-2">
                            {currentList.map((item, index) => (
                                <div key={index} className="flex items-start gap-3 text-right">
                                    <span className="text-blue-600 font-bold mt-1">•</span>
                                    <span className="text-gray-700 flex-1">{item.text || t('Empty item')}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-400">
                            <div className="flex flex-col items-center">
                                <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                                <p className="text-sm">{t('No items in list')}</p>
                            </div>
                        </div>
                    )}

                    {/* Edit Button - Appears on hover */}
                    {currentList.length > 0 && (
                        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('Edit List')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
            <div className="p-0.5">
                <div className="space-y-4">
                    {/* Add Item Input */}
                    <div className="flex gap-2">
                        <Input
                            value={text}
                            onChange={(value) => setText(value || '')}
                            placeholder={t('Enter list item...')}
                            onPressEnter={AddText}
                            className="flex-1"
                            style={{
                                padding: '10px 16px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                textAlign: 'right',
                            }}
                        />
                        <button
                            onClick={AddText}
                            disabled={!text.trim() || saving}
                            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                        >
                            <PlusIcon style={{ fontSize: 16 }} />
                            <span className="text-sm font-medium">{t('Add')}</span>
                        </button>
                    </div>

                    {/* List Items */}
                    {currentList.length > 0 ? (
                        <div className="space-y-2">
                            {currentList.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 group/item"
                                >
                                    {/* Item Number */}
                                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-semibold">
                                        {index + 1}
                                    </div>

                                    {/* Item Text Input */}
                                    <Input
                                        key={`list-item-${index}-${activeLang}`}
                                        value={item.text || ''}
                                        onChange={(value) => editItem(index, value || '')}
                                        placeholder={t('List item text...')}
                                        className="flex-1"
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            textAlign: 'right',
                                            backgroundColor: 'white',
                                        }}
                                    />

                                    {/* Actions */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        {/* Move Up */}
                                        <button
                                            onClick={() => moveItem(index, 'up')}
                                            disabled={movingIndex === index || index === 0}
                                            className="p-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title={t('Move up')}
                                        >
                                            {movingIndex === index ? (
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                </svg>
                                            )}
                                        </button>

                                        {/* Move Down */}
                                        <button
                                            onClick={() => moveItem(index, 'down')}
                                            disabled={movingIndex === index || index === currentList.length - 1}
                                            className="p-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title={t('Move down')}
                                        >
                                            {movingIndex === index ? (
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            )}
                                        </button>

                                        {/* Delete */}
                                        <button
                                            onClick={() => removeItem(index)}
                                            disabled={deletingIndex === index}
                                            className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={t('Delete')}
                                        >
                                            {deletingIndex === index ? (
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-400">
                            <div className="flex flex-col items-center">
                                <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                                <p className="text-sm">{t('No items yet. Add items above.')}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3 justify-end items-center">
                    {hasChanges && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                            {t('Unsaved changes')}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !hasChanges}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>{t('Saving...')}</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{t('Save')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
